import { createContext, ReactNode, createElement, useCallback, useContext, useEffect, useState } from 'react';
import { apiService, ApiResponse, PortfolioSummary, PortfolioSettings } from '../services/api';
import { useAuth } from './useAuth';
import { localStorageUtils } from '../utils/localStorage';
import { getStockDividendBatch, convertToYahooTicker, getStockPricesBatch } from '../utils/stockDataService';
import { getApiFailureMessage, getUnifiedApiErrorMessage } from '../utils/apiErrorMessage';

// 동일 훅 인스턴스가 여러 개 생겨도, 로그인 상태에서 `/portfolio/summary`는
// 짧은 시간 내 중복 네트워크 요청이 발생하지 않도록 dedupe 처리한다.
let portfolioSummaryInFlight: Promise<ApiResponse<PortfolioSummary>> | null = null;
let portfolioSummaryLastResponse: ApiResponse<PortfolioSummary> | null = null;
let portfolioSummaryLastFetchedAt = 0;

let portfolioSettingsInFlight: Promise<ApiResponse<PortfolioSettings>> | null = null;
let portfolioSettingsLastResponse: ApiResponse<PortfolioSettings> | null = null;
let portfolioSettingsLastFetchedAt = 0;

const PORTFOLIO_THROTTLE_MS = 1500;

const getPortfolioSummaryShared = (): Promise<ApiResponse<PortfolioSummary>> => {
  const now = Date.now();
  if (
    portfolioSummaryLastResponse &&
    now - portfolioSummaryLastFetchedAt < PORTFOLIO_THROTTLE_MS
  ) {
    return Promise.resolve(portfolioSummaryLastResponse);
  }

  if (!portfolioSummaryInFlight) {
    portfolioSummaryInFlight = apiService.getPortfolioSummary()
      .then((res) => {
        portfolioSummaryLastResponse = res;
        portfolioSummaryLastFetchedAt = Date.now();
        return res;
      })
      .finally(() => {
        portfolioSummaryInFlight = null;
      });
  }

  return portfolioSummaryInFlight;
};

const getPortfolioSettingsShared = (): Promise<ApiResponse<PortfolioSettings>> => {
  const now = Date.now();
  if (
    portfolioSettingsLastResponse &&
    now - portfolioSettingsLastFetchedAt < PORTFOLIO_THROTTLE_MS
  ) {
    return Promise.resolve(portfolioSettingsLastResponse);
  }

  if (!portfolioSettingsInFlight) {
    portfolioSettingsInFlight = apiService.getPortfolioSettings()
      .then((res) => {
        portfolioSettingsLastResponse = res;
        portfolioSettingsLastFetchedAt = Date.now();
        return res;
      })
      .finally(() => {
        portfolioSettingsInFlight = null;
      });
  }

  return portfolioSettingsInFlight;
};

const invalidatePortfolioSummaryCache = () => {
  portfolioSummaryLastResponse = null;
  portfolioSummaryLastFetchedAt = 0;
};

const invalidatePortfolioSettingsCache = () => {
  portfolioSettingsLastResponse = null;
  portfolioSettingsLastFetchedAt = 0;
};

export interface PortfolioState {
  summary: PortfolioSummary | null;
  settings: PortfolioSettings | null;
  isLoading: boolean;
  error: string | null;
}

export interface PortfolioActions {
  fetchPortfolioSummary: () => Promise<void>;
  fetchPortfolioSettings: () => Promise<void>;
  updatePortfolioSettings: (data: { totalSeed: number; currency: string }) => Promise<boolean>;
  clearError: () => void;
}

type PortfolioContextValue = PortfolioState & PortfolioActions;

const PortfolioContext = createContext<PortfolioContextValue | undefined>(undefined);

export const PortfolioProvider = ({ children }: { children: ReactNode }) => {
  const { isAuthenticated } = useAuth();
  const [summary, setSummary] = useState<PortfolioSummary | null>(null);
  const [settings, setSettings] = useState<PortfolioSettings | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 포트폴리오 요약 조회
  const fetchPortfolioSummary = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      if (isAuthenticated) {
        // 로그인한 경우: DB에서 조회
        const response = await getPortfolioSummaryShared();
        
        if (response.success && response.data) {
          setSummary(response.data);
          // 서버 응답을 로컬스토리지에도 저장 (직접 저장은 안 되지만, 투자 기록을 통해 계산 가능)
        } else {
          setError(getUnifiedApiErrorMessage(response));
        }
      } else {
        // 로그인하지 않은 경우: 로컬스토리지에서 조회 (요약은 계산 필요)
        // 투자 기록을 기반으로 요약 계산
        const localInvestments = localStorageUtils.getInvestments();
        
        // 매수 기록으로 총 투자금 계산 (총 시드 = 총 투자금액)
        const buyRecords = localInvestments.filter(inv => inv.type === 'BUY' && !inv.isDeleted);
        let totalSeed = 0; // 총 시드 = 총 투자금액
        let totalInvestment = 0;
        let totalProfitAmount = 0;
        let totalDividendAmount = 0;
        
        // 종목별 보유 수량 및 투자금액 계산
        const stockMap = new Map<string, {
          totalQuantity: number;
          totalAmount: number;
          averagePrice: number;
          stockCode: string;
          ticker?: string;
        }>();
        
        buyRecords.forEach(record => {
          if (record.pricePerShare && record.quantity) {
            const investmentAmount = record.pricePerShare * record.quantity;
            totalInvestment += investmentAmount;
            totalSeed += investmentAmount; // 총 시드 = 총 투자금액
            
            if (record.stockCode) {
              const key = record.stockCode;
              if (!stockMap.has(key)) {
                stockMap.set(key, {
                  totalQuantity: 0,
                  totalAmount: 0,
                  averagePrice: 0,
                  stockCode: key,
                  ticker: record.ticker || convertToYahooTicker(key)
                });
              }
              const stock = stockMap.get(key)!;
              stock.totalQuantity += record.quantity;
              stock.totalAmount += investmentAmount;
            }
          }
          
          // 배당 정보 (기록에 있는 경우)
          if (record.dividendPerShare && record.quantity) {
            totalDividendAmount += record.dividendPerShare * record.quantity;
          }
        });
        
        // 매도 기록으로 실현 손익 계산 및 보유 수량 차감
        const sellRecords = localInvestments.filter(inv => inv.type === 'SELL' && !inv.isDeleted);
        sellRecords.forEach(record => {
          if (record.realizedProfitAmount) {
            totalProfitAmount += record.realizedProfitAmount;
          }
          
          // 보유 수량 차감
          if (record.selectedStockId) {
            const buyRecord = buyRecords.find(r => r.id === record.selectedStockId);
            if (buyRecord && buyRecord.stockCode) {
              const stock = stockMap.get(buyRecord.stockCode);
              if (stock) {
                stock.totalQuantity -= (record.sellQuantity || record.quantity || 0);
                if (stock.totalQuantity < 0) stock.totalQuantity = 0;
              }
            }
          }
        });
        
        // 종가 정보를 가져와서 미실현 손익 계산
        const stockCodes = Array.from(stockMap.keys());
        const tickers = stockCodes.map(code => {
          const stock = stockMap.get(code);
          return stock?.ticker || convertToYahooTicker(code);
        });
        
        const priceMap = await getStockPricesBatch(stockCodes, tickers);
        
        // 배당 정보: 기록된 종목(고유 티커) 수만큼만 1회 조회
        const uniqueTickers = Array.from(new Set(
          Array.from(stockMap.values())
            .filter(stock => stock.totalQuantity > 0 && stock.ticker)
            .map(stock => stock.ticker as string)
        ));
        const dividendMap = await getStockDividendBatch(uniqueTickers);
        
        stockMap.forEach((stock, stockCode) => {
          if (stock.totalQuantity > 0 && stock.ticker && dividendMap[stock.ticker]) {
            const dividendInfo = dividendMap[stock.ticker]!;
            const annualDividend = dividendInfo.annualDividend || dividendInfo.dividendPerShare || 0;
            totalDividendAmount += annualDividend * stock.totalQuantity;
          }
        });
        
        // 평균 단가 계산 및 미실현 손익 계산
        let totalUnrealizedProfit = 0;
        stockMap.forEach((stock, stockCode) => {
          if (stock.totalQuantity > 0) {
            // 평균 단가 계산
            stock.averagePrice = stock.totalAmount / stock.totalQuantity;
            
            const priceInfo = priceMap[stockCode];
            if (priceInfo && priceInfo.closePrice > 0) {
              const currentValue = priceInfo.closePrice * stock.totalQuantity;
              const originalValue = stock.averagePrice * stock.totalQuantity;
              totalUnrealizedProfit += (currentValue - originalValue);
            }
          }
        });
        
        // 총 수익금액 = 실현 손익 + 미실현 손익
        totalProfitAmount += totalUnrealizedProfit;
        
        // 수익률 계산
        const totalProfitRate = totalSeed > 0 ? (totalProfitAmount / totalSeed) * 100 : 0;
        
        // 배당률 계산 (연배당률은 연간 배당금액 / 총 시드 * 100)
        const totalDividendRate = totalSeed > 0 ? (totalDividendAmount / totalSeed) * 100 : 0;
        
        const calculatedSummary: PortfolioSummary = {
          totalSeed,
          totalInvestment,
          totalProfitRate,
          totalDividendRate,
          totalProfitAmount,
          totalDividendAmount,
          lastUpdated: new Date().toISOString()
        };
        
        setSummary(calculatedSummary);
      }
    } catch (error: any) {
      console.error('포트폴리오 요약 조회 오류:', error);
      setError(getApiFailureMessage(error, '포트폴리오 요약을 불러오는 중 오류가 발생했습니다.'));
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  // 포트폴리오 설정 조회
  const fetchPortfolioSettings = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      if (isAuthenticated) {
        // 로그인한 경우: DB에서 조회
        const response = await getPortfolioSettingsShared();
        
        if (response.success && response.data) {
          setSettings(response.data);
          // 로그인한 경우 로컬스토리지에 저장하지 않음 (개인정보 보호)
        } else {
          setError(getUnifiedApiErrorMessage(response));
        }
      } else {
        // 로그인하지 않은 경우: 로컬스토리지에서 조회
        const localSettings = localStorageUtils.getPortfolioSettings();
        setSettings(localSettings);
      }
    } catch (error: any) {
      console.error('포트폴리오 설정 조회 오류:', error);
      setError(getApiFailureMessage(error, '포트폴리오 설정을 불러오는 중 오류가 발생했습니다.'));
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  // 포트폴리오 설정 수정
  const updatePortfolioSettings = useCallback(async (data: { 
    totalSeed: number; 
    currency: string; 
  }): Promise<boolean> => {
    try {
      setIsLoading(true);
      setError(null);

      if (isAuthenticated) {
        // 로그인한 경우: DB에 저장
        const response = await apiService.updatePortfolioSettings(data);
        
        if (response.success && response.data) {
          setSettings(response.data);
          // 로그인한 경우 로컬스토리지에 저장하지 않음 (개인정보 보호)
          // 방금 수정한 값이 바로 반영되도록 캐시 무효화
          invalidatePortfolioSettingsCache();
          invalidatePortfolioSummaryCache();
          await fetchPortfolioSummary();
          return true;
        } else {
          setError(getUnifiedApiErrorMessage(response));
          return false;
        }
      } else {
        // 로그인하지 않은 경우: 로컬스토리지에 저장
        localStorageUtils.setPortfolioSettings(data);
        // PortfolioSettings 타입에 맞게 변환
        const localSettings: PortfolioSettings = {
          totalSeed: data.totalSeed,
          currency: data.currency,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        setSettings(localSettings);
        return true;
      }
    } catch (error: any) {
      console.error('포트폴리오 설정 수정 오류:', error);
      setError(getApiFailureMessage(error, '포트폴리오 설정 수정 중 오류가 발생했습니다.'));
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [fetchPortfolioSummary, isAuthenticated]);

  // 에러 초기화
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // App 트리에서 Provider가 마운트되는 1곳에서만 초기 로드 수행
  useEffect(() => {
    fetchPortfolioSummary();
    fetchPortfolioSettings();
  }, [fetchPortfolioSummary, fetchPortfolioSettings]);

  const value: PortfolioContextValue = {
    summary,
    settings,
    isLoading,
    error,
    fetchPortfolioSummary,
    fetchPortfolioSettings,
    updatePortfolioSettings,
    clearError,
  };

  return createElement(PortfolioContext.Provider, { value }, children);
};

export const usePortfolio = (): PortfolioContextValue => {
  const ctx = useContext(PortfolioContext);
  if (!ctx) {
    throw new Error('usePortfolio는 PortfolioProvider 내부에서 사용되어야 합니다.');
  }
  return ctx;
};
